import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Helmet from 'react-helmet';
import { withRouteData } from 'react-static';
import ReactTable from 'react-table';

import BlockSubtitle from '../global/typography/BlockSubtitle';
import BlockTitle from '../global/typography/BlockTitle';
import CardContainer from '../global/CardContainer';
import Grid from '../Admin/Grid/styler';
import NavLink from '../global/NavLink';
import P from '../global/typography/P';
import Page from '../global/Page';
import ClearButton from '../global/Button/ClearButton';
import SmallButton from '../global/Button/SmallButton';

const Description = styled.p`
  color: ${props => props.theme.color.textSecondary};
  font-size: 16px;
  font-family: ${props => props.theme.font.sourceSans};
  font-weight: ${props => props.theme.fontWeight.regular};
  line-height: 1.55;
  letter-spacing: -0.26px;
  margin: 9px 0 13px 0;
`;

const GridStyler = styled(Grid)`
  margin-bottom: 13px;

  .ReactTable .rt-thead .rt-th.-cursor-pointer,
  .ReactTable .rt-thead .rt-td.-cursor-pointer {
    cursor: default;
  }

  .ReactTable .rt-tbody .rt-tr-group {
    border-bottom: solid 1px rgba(0, 0, 0, 0.05);
    cursor: default;
  }

  .ReactTable .rt-tbody .rt-expandable {
    cursor: default;
  }
`;

const Header = styled.h3`
  color: ${props => props.theme.color.headerSecondary};
  font-size: 14px;
  font-family: ${props => props.theme.font.sourceSans};
  font-weight: ${props => props.theme.fontWeight.bold};
  line-height: 1.25;
  text-transform: uppercase;
  margin: 0;
  margin-bottom: 16px;
  span {
    display: inline-block;
    line-height: 1.2;
    padding-bottom: 1px;
    + span {
      border-left: 2px solid ${props => props.theme.color.headerSecondary};
      padding-left: 6px;
      margin-left: 6px;
    }
  }
`;

const ImageWrapper = styled.figure`
  width: 100%;
  height: 0;
  padding-top: calc(9 / 16 * 100%);
  position: relative;
  margin: 0;
`;

const Image = styled.img`
  height: 100%;
  width: 100%;
  object-fit: contain;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const JobsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: -10px;

  & > div {
    margin-top: 25px;
    padding-right: 40px;
    width: 50%;

    @media (max-width: ${props => props.theme.deviceWidth.tablet}) {
      margin-bottom: 25px;
      width: 100%;
    }

    @media (max-width: ${props => props.theme.deviceWidth.mobilePhone}) {
      max-width: none;
      margin-top: 30px;
      padding-right: 0;
    }
  }
`;

const Subtitle = styled(P)`
  margin: 20px 0 20px 0;
`;

const HeaderWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ColumnHeading = styled.div`
  display: flex;
`;

const ApplyButton = styled(ClearButton)`
  width: fit-content;
  padding: 8px;
`;

export const ApplyAllButton = styled(ClearButton)`
  border: none;
  color: ${props => props.theme.color.primary};
  font-size: 16px;
  font-weight: ${props => props.theme.fontWeight.regular};
  width: auto;
  text-transform: initial;
`;

const columns = [
  {
    accessor: 'name',
    Cell: row => <P>{row.value}</P>,
    Header: () => <ColumnHeading>Available Opportunity</ColumnHeading>,
    width: 300
  },
  {
    accessor: 'shift',
    Cell: row => <P>{row.value}</P>,
    Header: 'Shift',
    style: { display: 'flex', justifyContent: 'center' },
    width: 35
  },
  {
    accessor: 'link',
    Cell: row =>
      row.value !== null && (
        <NavLink to={row.value}>
          <ApplyButton>Apply Now</ApplyButton>
        </NavLink>
      ),
    style: { display: 'flex', justifyContent: 'center' }
  }
];

const Careers = ({ careers, pageData }) => (
  <Page>
    <Helmet
      title={pageData.seo.title}
      meta={[
        { property: 'og:title', content: 'Careers' },
        { property: 'description', content: pageData.seo.description }
      ]}
    />
    <CardContainer pad>
      <BlockTitle>Careers</BlockTitle>
      <Subtitle>
        Lochinvar provides a wonderful working environment and competitive pay.
        If you are looking to start a new career or to upgrade an existing one,
        use the links below to view open positions at Lochinvar and become part
        of our family.
      </Subtitle>
      <BlockSubtitle>Job Openings</BlockSubtitle>
      <JobsWrapper>
        <div>
          <HeaderWrap>
            <Header>Lochinvar Plant Opportunities</Header>
          </HeaderWrap>
          <ImageWrapper>
            <Image src="/careers/plant-image.jpg" alt="Plant employees" />
          </ImageWrapper>
          <Description>{pageData.plantCopy}</Description>
          {careers.length > 0 && (
            <GridStyler>
              <ReactTable
                columns={columns}
                data={careers}
                defaultPageSize={Math.min(careers.length, 10)}
                resizable={false}
                showPageSizeOptions={false}
                showPagination={careers.length > 10}
                sortable={false}
              />
            </GridStyler>
          )}
          <NavLink to="https://jobs.aosmith.com/search/?q=&q2=&alertId=&locationsearch=lebanon&geolocation=&searchby=location&d=10&lat=&lon=&title=&location=&facility=&shifttype=Manufacturing">
            <SmallButton>View All Openings</SmallButton>
          </NavLink>
        </div>
        <div>
          <Header>Lochinvar Office Opportunities</Header>
          <ImageWrapper>
            <Image src="/careers/corporate-image.jpg" alt="Corporate office" />
          </ImageWrapper>
          <Description>{pageData.officeCopy}</Description>
          <NavLink to="https://jobs.aosmith.com/search/?searchby=location&createNewAlert=false&q=&locationsearch=Lebanon&geolocation=">
            <SmallButton>View All Openings</SmallButton>
          </NavLink>
        </div>
      </JobsWrapper>
    </CardContainer>
  </Page>
);

Careers.propTypes = {
  careers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      shift: PropTypes.string
    })
  ),
  pageData: PropTypes.shape({
    officeCopy: PropTypes.string,
    plantCopy: PropTypes.string
  })
};

export default withRouteData(Careers);
